import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Search, FileText, X, Columns3, Wrench, RefreshCw } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrgId } from "../hooks/useOrgId";
import { useAutoSyncRefresh } from "../hooks/useAutoSyncRefresh";
import * as api from "../lib/api";
import { SortIcon } from "../components/search-performance/shared";

const PRIMARY = "#018D4C";
const PRIMARY_LIGHT = "#E6F5EE";

const CLUSTER_COLORS = [
  "#018D4C", "#FF2A00", "#FF8C00", "#9B59B6", "#1ABC9C",
  "#E67E22", "#3498DB", "#E74C3C", "#2ECC71", "#8E44AD",
];

function gradeColor(grade) {
  return grade === "strong" ? "#008A33"
    : grade === "moderate" ? "#FF8C00"
    : grade === "weak" ? "#E67E22"
    : "#FF2A00";
}

function ContentScorePill({ score, grade }) {
  if (score === undefined || score === null) return <span className="text-xs text-zinc-400">—</span>;
  const c = gradeColor(grade);
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold tabular-nums rounded-full"
      style={{ background: c + "15", color: c }}
      data-testid="content-score-pill"
      title={`Content score: ${score} (${grade})`}
    >
      {Math.round(score)}
    </span>
  );
}

function AEOScorePill({ score, grade }) {
  if (score === undefined || score === null) return <span className="text-xs text-zinc-400">—</span>;
  const c = gradeColor(grade);
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold tabular-nums rounded-full"
      style={{ background: c + "15", color: c }}
      title={`AEO score: ${score} (${grade})`}
    >
      {Math.round(score)}
    </span>
  );
}

// Mirrors backend/services/rework_service.py thresholds.
const THIN_WORDS = 800;
const VERY_THIN_WORDS = 300;

function WordCount({ value }) {
  if (value === undefined || value === null) return <span className="text-xs text-zinc-400">—</span>;
  const color = value < VERY_THIN_WORDS ? "#FF2A00" : value < THIN_WORDS ? "#E67E22" : undefined;
  return (
    <span
      className="text-sm tabular-nums"
      style={{ color: color || "#18181b", fontWeight: color ? 700 : 400 }}
      title={value < VERY_THIN_WORDS ? "Very thin content" : value < THIN_WORDS ? `Thin content — aim for ${THIN_WORDS}+ words` : undefined}
      data-testid="article-word-count"
    >
      {value.toLocaleString()}
    </span>
  );
}

// Rework action from the list payload: Expand (thin) or Rewrite (underperforming), with
// priority = content gap weighted by search demand. Reasons go in the tooltip.
function ReworkBadge({ rework }) {
  if (!rework?.action) return <span className="text-xs text-zinc-300">—</span>;
  const expand = rework.action === "expand";
  const c = rework.priority >= 60 ? "#FF2A00" : rework.priority >= 30 ? "#E67E22" : "#71717a";
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap"
      title={rework.reasons?.join("\n")}
      data-testid={`rework-${rework.action}`}
    >
      <span
        className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded"
        style={expand ? { background: "#FFF4E5", color: "#B45309" } : { background: "#EEF2FF", color: "#4338CA" }}
      >
        {expand ? "Expand" : "Rewrite"}
      </span>
      <span className="text-xs font-bold tabular-nums" style={{ color: c }}>{rework.priority}</span>
    </span>
  );
}

function LangBadge({ lang }) {
  if (!lang) return null;
  return (
    <span
      className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded border border-zinc-200 text-zinc-500 shrink-0"
      title={`Language: ${lang}`}
      data-testid="article-lang-badge"
    >
      {lang}
    </span>
  );
}

// Fallback when the cluster has no label yet: compress `ORG_..._CLUSTER_N` to `Cluster N`.
function clusterLabel(clusterId, label) {
  if (label) return label;
  if (!clusterId) return null;
  const m = clusterId.match(/_CLUSTER_(\d+)$/i);
  return m ? `Cluster ${m[1]}` : clusterId;
}

function clusterColor(clusterId, order) {
  const i = order.indexOf(clusterId);
  if (i >= 0) return CLUSTER_COLORS[i % CLUSTER_COLORS.length];
  let h = 0;
  for (const ch of clusterId) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CLUSTER_COLORS[h % CLUSTER_COLORS.length];
}

function Num({ value, format = n => Math.round(n).toLocaleString() }) {
  if (value === undefined || value === null) return <span className="text-xs text-zinc-400">—</span>;
  return <span className="text-sm text-zinc-900 tabular-nums">{format(value)}</span>;
}

// GSC writes 0 / 0.0 for articles it found no page row for — show those as blank, not "0".
function gscValue(art, key) {
  return art.gsc_matched === true ? art[key] : null;
}

function SortTh({ col, label, sort, order, onSort, align = "left", className = "" }) {
  return (
    <th
      className={`py-3 px-3 text-[11px] font-bold text-zinc-400 uppercase tracking-widest cursor-pointer select-none whitespace-nowrap hover:text-zinc-700 ${align === "right" ? "text-right" : "text-left"} ${className}`}
      onClick={() => onSort(col)}
      data-testid={`sort-${col}`}
    >
      <span className={`inline-flex items-center gap-0.5 ${align === "right" ? "justify-end" : ""}`}>
        {label}
        <SortIcon col={col} sort={sort} order={order} />
      </span>
    </th>
  );
}

function FilterSelect({ value, onChange, options, testId, label }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      data-testid={testId}
      aria-label={label}
      className="px-2.5 py-1.5 text-xs border border-zinc-100 bg-white rounded-lg text-zinc-700 focus:outline-none focus:ring-2"
      style={{ "--tw-ring-color": PRIMARY, color: value ? PRIMARY : undefined, fontWeight: value ? 700 : 400 }}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// Sortable label inside a shared header cell (used by the merged Score/AEO column).
function SortSpan({ col, label, sort, order, onSort }) {
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      data-testid={`sort-${col}`}
      className="inline-flex items-center gap-0.5 cursor-pointer hover:text-zinc-700"
    >
      {label}
      <SortIcon col={col} sort={sort} order={order} />
    </button>
  );
}

// Publish status from the CMS: `publish` = live on the site, `draft` = not public yet.
// Drafts are clickable: first click asks to confirm, second pushes the article live via the CMS.
function StatusBadge({ status, onPublish }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (status === "draft") {
    const stop = e => e.stopPropagation();
    if (busy) {
      return <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 shrink-0">Publishing…</span>;
    }
    if (confirming) {
      const publish = async e => {
        stop(e);
        setBusy(true);
        setError(null);
        try {
          await onPublish();
        } catch (err) {
          setError(err.response?.data?.detail || "Publish failed");
          setConfirming(false);
        } finally {
          setBusy(false);
        }
      };
      return (
        <span className="inline-flex items-center gap-1 shrink-0" onClick={stop}>
          <button
            type="button"
            onClick={publish}
            className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded text-white"
            style={{ background: PRIMARY }}
            data-testid="article-publish-confirm"
          >
            Publish live
          </button>
          <button
            type="button"
            onClick={e => { stop(e); setConfirming(false); }}
            className="text-zinc-400 hover:text-zinc-700"
            aria-label="Cancel publish"
          >
            <X size={11} strokeWidth={2.5} />
          </button>
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={e => { stop(e); setConfirming(true); setError(null); }}
        className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded shrink-0 hover:ring-1 ${
          error ? "bg-red-100 text-red-700 ring-red-300" : "bg-amber-100 text-amber-700 hover:ring-amber-300"
        }`}
        title={error ? `${error} — click to retry` : "Draft — click to publish on the site"}
        data-testid="article-status-draft"
      >
        Draft
      </button>
    );
  }
  if (status === "publish") {
    return (
      <span
        className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-green-50 text-green-700 shrink-0"
        title="Live — published on the site"
        data-testid="article-status-live"
      >
        Live
      </span>
    );
  }
  return null;
}

// Compact index-status indicator on the title line; title attribute carries the full status.
function IndexDot({ art }) {
  if (art.gsc_matched === true) {
    return <span data-testid="article-index-dot" title="Indexed" className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />;
  }
  if (art.indexing_submitted_at) {
    return <span data-testid="article-index-dot" title="Submitted for indexing" className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />;
  }
  if (art.gsc_matched === false) {
    return <span data-testid="article-index-dot" title="Not indexed" className="h-1.5 w-1.5 rounded-full bg-orange-500 shrink-0" />;
  }
  return null;
}

function IndexBadge({ art }) {
  if (art.gsc_matched === true) {
    return <span className="px-2 py-0.5 text-[9px] font-bold rounded-full bg-green-100 text-green-700 uppercase tracking-widest">Indexed</span>;
  }
  if (art.indexing_submitted_at) {
    return <span className="px-2 py-0.5 text-[9px] font-bold rounded-full bg-blue-100 text-blue-700 uppercase tracking-widest">Submitted</span>;
  }
  if (art.gsc_matched === false) {
    return <span className="px-2 py-0.5 text-[9px] font-bold rounded-full bg-orange-100 text-orange-700 uppercase tracking-widest whitespace-nowrap">Not indexed</span>;
  }
  return <span className="text-xs text-zinc-400">—</span>;
}

function PersonaBadge({ personaFit }) {
  const results = personaFit?.results || [];
  const best = results.find(r => r.persona === personaFit?.best_match) || results[0];
  if (!best) {
    return (
      <span
        className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-full bg-zinc-100 text-zinc-400"
        title="Not scored against any buyer persona yet — run Persona Fit on the article page"
        data-testid="persona-badge-general"
      >
        General
      </span>
    );
  }
  const c = gradeColor(best.verdict);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-full"
      style={{ background: c + "15", color: c }}
      title={`${best.persona} — ${best.score}/100 (${best.verdict})`}
      data-testid="persona-badge"
    >
      {best.persona}
    </span>
  );
}

function TopicChip({ label, primary = false }) {
  if (!label) return null;
  return (
    <span
      className={`px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-full ${
        primary
          ? "bg-zinc-900 text-white"
          : "bg-white border border-zinc-100 text-zinc-500"
      }`}
      data-testid={primary ? "primary-topic-chip" : "topic-keyword-chip"}
    >
      {label}
    </span>
  );
}

const PER_PAGE = 25;

const INDEX_OPTIONS = [
  { value: "", label: "Any index status" },
  { value: "indexed", label: "Indexed" },
  { value: "not_indexed", label: "Not indexed" },
  { value: "submitted", label: "Submitted" },
];
const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "published", label: "Live" },
  { value: "draft", label: "Draft" },
];
const PERSONA_OPTIONS = [
  { value: "", label: "Any persona" },
  { value: "general", label: "General (unscored)" },
  { value: "scored", label: "Persona scored" },
];
const LENGTH_OPTIONS = [
  { value: "", label: "Any length" },
  { value: "under", label: `Thin (<${THIN_WORDS} words)` },
  { value: "very_thin", label: `Very thin (<${VERY_THIN_WORDS})` },
  { value: "thin", label: `Short (${VERY_THIN_WORDS}–${THIN_WORDS - 1})` },
  { value: "ok", label: `${THIN_WORDS}+ words` },
];
const REWORK_OPTIONS = [
  { value: "", label: "Any rework" },
  { value: "any", label: "Needs rework" },
  { value: "expand", label: "Expand (thin)" },
  { value: "rewrite", label: "Rewrite" },
];
const GRADE_OPTIONS = [
  { value: "", label: "Any score" },
  { value: "strong", label: "Strong" },
  { value: "moderate", label: "Moderate" },
  { value: "weak", label: "Weak" },
  { value: "poor", label: "Poor" },
];

// Text columns start A→Z; metric columns start high→low. Words starts shortest-first to surface thin content.
const ASC_FIRST = new Set(["title", "position", "words"]);

// Opt-in table columns, off by default so the table fits without horizontal scroll.
// GSC-dependent ones are only offered when Search Console is connected.
const OPTIONAL_COLUMNS = [
  { key: "ctr", label: "CTR", gsc: true },
  { key: "position", label: "Position", gsc: true },
  { key: "persona", label: "Persona" },
  { key: "index", label: "Index status", gsc: true },
];

const COLUMNS_KEY = "articles:columns";

function loadColumns() {
  try {
    const saved = JSON.parse(localStorage.getItem(COLUMNS_KEY) || "{}");
    return {
      ctr: !!saved.ctr,
      position: !!saved.position,
      persona: !!saved.persona,
      index: !!saved.index,
    };
  } catch {
    return { ctr: false, position: false, persona: false, index: false };
  }
}

export default function Articles() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const clusterId = searchParams.get("cluster") || "";
  const [data, setData] = useState({ articles: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [gsc, setGsc] = useState({ connected: false, daysBack: null });
  const [clusters, setClusters] = useState([]);
  const [sort, setSort] = useState("updated");
  const [order, setOrder] = useState("desc");
  const [indexFilter, setIndexFilter] = useState("");
  const [personaFilter, setPersonaFilter] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [lengthFilter, setLengthFilter] = useState("");
  const [reworkFilter, setReworkFilter] = useState("");
  const [columns, setColumns] = useState(loadColumns);

  const toggleColumn = key => {
    setColumns(c => {
      const next = { ...c, [key]: !c[key] };
      try { localStorage.setItem(COLUMNS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getArticles({
        page,
        per_page: PER_PAGE,
        search: search || undefined,
        cluster_id: clusterId || undefined,
        sort,
        order,
        index: indexFilter || undefined,
        persona: personaFilter || undefined,
        grade: gradeFilter || undefined,
        status: statusFilter || undefined,
        length: lengthFilter || undefined,
        rework: reworkFilter || undefined,
      });
      setData(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, search, clusterId, sort, order, indexFilter, personaFilter, gradeFilter, statusFilter, lengthFilter, reworkFilter]);

  const orgId = useOrgId();
  useEffect(() => { load(); }, [load, orgId]);
  // Reload when a background auto-sync finishes.
  useAutoSyncRefresh(load);

  // Refresh = pull changes from the CMS (incremental sync, e.g. drafts published there),
  // poll until the sync ends, then reload the list.
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const pollRef = useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);
  const refresh = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncError(null);
    try {
      await api.startSync();
    } catch (e) {
      setSyncError(e.response?.data?.detail || "Sync failed to start");
      setSyncing(false);
      load();
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const s = await api.getSyncStatus();
        if (s.status === "running") return;
        clearInterval(pollRef.current);
        if (s.status === "failed") setSyncError(s.message || "Sync failed");
        setSyncing(false);
        load();
      } catch {
        // transient poll error — keep polling
      }
    }, 2000);
  };
  useEffect(() => { setPage(1); }, [clusterId, sort, order, indexFilter, personaFilter, gradeFilter, statusFilter, lengthFilter, reworkFilter]);

  useEffect(() => {
    api.getGscSettings().then(s => {
      setGsc({
        connected: !!(s.verified && s.configured),
        daysBack: s.last_refresh?.days_back ?? null,
      });
    }).catch(() => {});
    api.getClusters().then(r => setClusters(r.clusters || [])).catch(() => {});
  }, [orgId]);

  const clusterOrder = clusters.map(c => c.cluster_id);
  const activeCluster = clusters.find(c => c.cluster_id === clusterId);

  const setClusterFilter = id => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("cluster", id); else next.delete("cluster");
    setSearchParams(next);
  };

  const handleSort = col => {
    if (col === sort) setOrder(o => (o === "asc" ? "desc" : "asc"));
    else { setSort(col); setOrder(ASC_FIRST.has(col) ? "asc" : "desc"); }
  };

  // Pushes the draft live through the CMS, then flips the row in place (no refetch).
  const publishArticle = async wpPostId => {
    const result = await api.updateArticleStatus(wpPostId, "publish");
    setData(d => ({
      ...d,
      articles: d.articles.map(a => (a.wp_post_id === wpPostId ? { ...a, status: result.status || "publish" } : a)),
    }));
  };

  const handleSearch = e => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const hasFilters = !!(clusterId || indexFilter || personaFilter || gradeFilter || statusFilter || lengthFilter || reworkFilter || search);
  const clearAll = () => {
    setIndexFilter(""); setPersonaFilter(""); setGradeFilter(""); setStatusFilter("");
    setLengthFilter(""); setReworkFilter("");
    setSearch(""); setSearchInput("");
    setClusterFilter("");
  };

  // One-click "what should I fix first": pages needing work, highest priority first.
  const reworkQueueOn = reworkFilter === "any" && sort === "priority" && order === "desc";
  const toggleReworkQueue = () => {
    if (reworkQueueOn) { setReworkFilter(""); setSort("updated"); setOrder("desc"); }
    else { setReworkFilter("any"); setSort("priority"); setOrder("desc"); }
  };

  const period = gsc.daysBack ? `${gsc.daysBack}d` : "";
  const sortProps = { sort, order, onSort: handleSort };

  return (
    <div data-testid="articles-page" className="p-6 sm:p-8 max-w-7xl mx-auto">
      <div className="mb-6 pb-6 border-b border-zinc-100">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">Content Library</p>
        <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">
          Articles
        </h1>
        {clusterId && (
          <div className="flex items-center gap-1.5 mt-2" data-testid="cluster-filter-chip">
            <span
              className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold tracking-wider uppercase rounded-full"
              style={{ background: PRIMARY_LIGHT, color: PRIMARY }}
            >
              Cluster: {clusterLabel(clusterId, activeCluster?.label)}
              <button
                onClick={() => setClusterFilter("")}
                data-testid="clear-cluster-filter"
                className="hover:opacity-60"
              >
                <X size={11} strokeWidth={2.5} />
              </button>
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="article-filters">
        <button
          type="button"
          onClick={toggleReworkQueue}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-lg border transition-colors"
          style={reworkQueueOn
            ? { background: PRIMARY, borderColor: PRIMARY, color: "#fff" }
            : { background: "#fff", borderColor: "#f4f4f5", color: "#3f3f46" }}
          title="Thin or underperforming articles, highest priority first (priority weighs the content gap by search demand)"
          data-testid="rework-queue-toggle"
        >
          <Wrench size={12} strokeWidth={2.25} />
          Rework priority
        </button>
        <form onSubmit={handleSearch} className="relative" data-testid="article-search-form">
          <Search size={14} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search articles…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            data-testid="article-search-input"
            className="pl-8 pr-7 py-1.5 text-sm border border-zinc-100 bg-white rounded-lg focus:outline-none focus:ring-2 w-52"
            style={{ "--tw-ring-color": PRIMARY }}
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => { setSearch(""); setSearchInput(""); setPage(1); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
              aria-label="Clear search"
              data-testid="clear-search"
            >
              <X size={13} strokeWidth={2} />
            </button>
          )}
        </form>
        <FilterSelect
          label="Cluster"
          testId="filter-cluster"
          value={clusterId}
          onChange={setClusterFilter}
          options={[
            { value: "", label: "All clusters" },
            ...clusters.map(c => ({ value: c.cluster_id, label: `${clusterLabel(c.cluster_id, c.label)} (${c.size ?? 0})` })),
          ]}
        />
        <FilterSelect label="Publish status" testId="filter-status" value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
        {gsc.connected && (
          <FilterSelect label="Index status" testId="filter-index" value={indexFilter} onChange={setIndexFilter} options={INDEX_OPTIONS} />
        )}
        <FilterSelect label="Content score" testId="filter-grade" value={gradeFilter} onChange={setGradeFilter} options={GRADE_OPTIONS} />
        <FilterSelect label="Length" testId="filter-length" value={lengthFilter} onChange={setLengthFilter} options={LENGTH_OPTIONS} />
        <FilterSelect label="Rework" testId="filter-rework" value={reworkFilter} onChange={setReworkFilter} options={REWORK_OPTIONS} />
        <FilterSelect label="Persona" testId="filter-persona" value={personaFilter} onChange={setPersonaFilter} options={PERSONA_OPTIONS} />
        {hasFilters && (
          <button onClick={clearAll} className="text-xs text-zinc-500 hover:text-zinc-900 underline ml-1" data-testid="clear-all-filters">
            Clear filters
          </button>
        )}
        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            onClick={refresh}
            disabled={syncing}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-zinc-100 bg-white rounded-lg text-zinc-600 hover:bg-zinc-50 disabled:opacity-60 transition-colors"
            style={syncError ? { borderColor: "#fca5a5", color: "#b91c1c" } : undefined}
            title={syncError ? `${syncError} — click to retry` : "Sync from your CMS and reload (picks up drafts published on the site)"}
            data-testid="articles-refresh"
          >
            <RefreshCw size={13} strokeWidth={2} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing…" : "Refresh"}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-zinc-100 bg-white rounded-lg text-zinc-600 hover:bg-zinc-50 transition-colors"
                data-testid="columns-toggle"
              >
                <Columns3 size={13} strokeWidth={2} />
                Columns
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-zinc-400">Show columns</DropdownMenuLabel>
              {OPTIONAL_COLUMNS.filter(c => !c.gsc || gsc.connected).map(c => (
                <DropdownMenuCheckboxItem
                  key={c.key}
                  checked={columns[c.key]}
                  onCheckedChange={() => toggleColumn(c.key)}
                  className="text-xs"
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="text-xs text-zinc-400 tabular-nums">{data.total.toLocaleString()} articles</span>
        </div>
      </div>

      {!gsc.connected && (
        <div className="mb-4 px-4 py-2.5 text-xs text-zinc-600 bg-white border border-zinc-100 rounded-xl" data-testid="gsc-connect-hint">
          Connect Search Console to see clicks, impressions, CTR and position for each article.{" "}
          <button onClick={() => navigate("/dashboard/settings/search-console")} className="font-medium underline" style={{ color: PRIMARY }}>
            Connect
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-zinc-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-400">Loading articles...</div>
        ) : data.articles.length === 0 ? (
          <div className="p-12 text-center">
            <FileText size={40} className="text-zinc-300 mx-auto" />
            <p className="text-sm text-zinc-500 mt-4">
              {hasFilters ? "No articles match these filters."
                : "No articles yet. Sync your CMS first."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="sticky top-0 z-10 bg-zinc-50/90 backdrop-blur-sm">
                <tr className="border-b border-zinc-200">
                  <SortTh col="title" label="Title" {...sortProps} className="px-4" />
                  <th className="text-left py-3 px-3 text-[11px] font-bold text-zinc-400 uppercase tracking-widest hidden sm:table-cell">Cluster</th>
                  {gsc.connected && (
                    <>
                      <SortTh col="clicks" label={`Clicks${period ? ` ${period}` : ""}`} align="right" {...sortProps} className="hidden md:table-cell" />
                      <SortTh col="impressions" label="Impr." align="right" {...sortProps} className="hidden md:table-cell" />
                      {columns.ctr && <SortTh col="ctr" label="CTR" align="right" {...sortProps} className="hidden md:table-cell" />}
                      {columns.position && <SortTh col="position" label="Pos." align="right" {...sortProps} className="hidden md:table-cell" />}
                    </>
                  )}
                  <SortTh col="words" label="Words" align="right" {...sortProps} className="hidden md:table-cell" />
                  <SortTh col="priority" label="Rework" {...sortProps} className="hidden md:table-cell" />
                  <th className="py-3 px-3 text-right text-[11px] font-bold text-zinc-400 uppercase tracking-widest hidden md:table-cell">
                    <span className="inline-flex items-center gap-2.5">
                      <SortSpan col="score" label="Score" {...sortProps} />
                      <SortSpan col="aeo" label="AEO" {...sortProps} />
                    </span>
                  </th>
                  {columns.persona && (
                    <th className="text-left py-3 px-3 text-[11px] font-bold text-zinc-400 uppercase tracking-widest hidden md:table-cell">Persona</th>
                  )}
                  {gsc.connected && columns.index && (
                    <th className="text-center py-3 px-3 text-[11px] font-bold text-zinc-400 uppercase tracking-widest hidden md:table-cell">Index</th>
                  )}
                  <SortTh col="updated" label="Updated" {...sortProps} className="hidden lg:table-cell" />
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {data.articles.map(art => {
                  const cColor = art.cluster_id ? clusterColor(art.cluster_id, clusterOrder) : null;
                  return (
                  <tr
                    key={art.wp_post_id}
                    data-testid={`article-row-${art.wp_post_id}`}
                    onClick={() => navigate(`/dashboard/articles/${art.wp_post_id}`)}
                    className="cursor-pointer hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0"
                  >
                    <td className="py-3 px-4">
                      <span className="flex items-center gap-1.5">
                        {gsc.connected && art.status !== "draft" && <IndexDot art={art} />}
                        <StatusBadge status={art.status} onPublish={() => publishArticle(art.wp_post_id)} />
                        <LangBadge lang={art.language} />
                        <span className="font-medium text-zinc-900 line-clamp-1">{art.title}</span>
                      </span>
                      <span className="flex items-center gap-1.5 mt-1">
                        {art.primary_topic && <TopicChip label={art.primary_topic} primary />}
                        {art.categories?.length > 0 && (
                          <span className="text-[10px] text-zinc-400">{art.categories.slice(0, 2).join(", ")}</span>
                        )}
                      </span>
                    </td>
                    <td className="py-3 px-3 hidden sm:table-cell">
                      {art.cluster_id ? (
                        <button
                          onClick={e => { e.stopPropagation(); setClusterFilter(art.cluster_id); }}
                          className="px-2 py-0.5 text-[10px] font-bold rounded-full whitespace-nowrap max-w-[11rem] truncate hover:opacity-75"
                          style={{ background: cColor + "15", color: cColor }}
                          title={`Filter by this cluster (${art.cluster_id})`}
                          data-testid={`article-cluster-${art.wp_post_id}`}
                        >
                          {clusterLabel(art.cluster_id, art.cluster_label)}
                        </button>
                      ) : (
                        <span className="text-xs text-zinc-400">—</span>
                      )}
                    </td>
                    {gsc.connected && (
                      <>
                        <td className="py-3 px-3 text-right hidden md:table-cell">
                          <Num value={gscValue(art, "gsc_clicks_28d")} />
                        </td>
                        <td className="py-3 px-3 text-right hidden md:table-cell">
                          <Num value={gscValue(art, "gsc_impressions_28d")} />
                        </td>
                        {columns.ctr && (
                          <td className="py-3 px-3 text-right hidden md:table-cell">
                            <Num value={gscValue(art, "gsc_ctr")} format={n => (n * 100).toFixed(1) + "%"} />
                          </td>
                        )}
                        {columns.position && (
                          <td className="py-3 px-3 text-right hidden md:table-cell">
                            <Num value={gscValue(art, "gsc_position")} format={n => n.toFixed(1)} />
                          </td>
                        )}
                      </>
                    )}
                    <td className="py-3 px-3 text-right hidden md:table-cell">
                      <WordCount value={art.word_count} />
                    </td>
                    <td className="py-3 px-3 hidden md:table-cell">
                      <ReworkBadge rework={art.rework} />
                    </td>
                    <td className="py-3 px-3 hidden md:table-cell">
                      <span className="flex items-center justify-end gap-1.5">
                        <ContentScorePill score={art.content_score} grade={art.content_grade} />
                        <AEOScorePill score={art.aeo_score} grade={art.aeo_grade} />
                      </span>
                    </td>
                    {columns.persona && (
                      <td className="py-3 px-3 hidden md:table-cell">
                        <PersonaBadge personaFit={art.persona_fit} />
                      </td>
                    )}
                    {gsc.connected && columns.index && (
                      <td className="py-3 px-3 text-center hidden md:table-cell">
                        <IndexBadge art={art} />
                      </td>
                    )}
                    <td className="py-3 px-3 hidden lg:table-cell">
                      <span className="text-xs text-zinc-400 whitespace-nowrap">{art.wp_updated_at?.slice(0, 10) || "—"}</span>
                    </td>
                    <td className="py-3 px-4">
                      {art.wp_url && (
                        <a
                          href={art.wp_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="opacity-50 hover:opacity-100 transition-opacity"
                          data-testid={`article-external-${art.wp_post_id}`}
                        >
                          <ArrowUpRight size={14} className="text-zinc-900" />
                        </a>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-zinc-500">
            Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, data.total)} of {data.total}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="prev-page"
              className="px-3 py-1.5 text-xs border border-zinc-100 disabled:opacity-40 hover:bg-zinc-50 rounded-lg transition-colors"
            >
              Prev
            </button>
            <span className="px-3 py-1.5 text-xs bg-zinc-900 text-white font-bold rounded-lg">{page}</span>
            <button
              onClick={() => setPage(p => Math.min(data.pages, p + 1))}
              disabled={page === data.pages}
              data-testid="next-page"
              className="px-3 py-1.5 text-xs border border-zinc-100 disabled:opacity-40 hover:bg-zinc-50 rounded-lg transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
